import { normalizeStatus, type MatchStatus } from "./matchStatus";

export type FeaturedCategory = "live" | "upcoming" | "completed" | "empty";

export interface FeaturedMatchInput {
  id: string;
  status: string;
  scheduledAt?: string | null;
  completedAt?: string | null;
  updatedAt?: string;
}

export interface FeaturedMatch<T extends FeaturedMatchInput> {
  match: T;
  category: FeaturedCategory;
}

function hasScoringActivity(match: FeaturedMatchInput): boolean {
  const any = match as any;
  if (typeof any.hasActivity === "boolean" && any.hasActivity) return true;
  if (typeof any.legalBalls === "number" && any.legalBalls > 0) return true;
  return false;
}

/**
 * Select a single featured match using strict priority:
 *   1. LIVE   — any genuine live match (first by most recent updatedAt)
 *   2. UPCOMING — nearest future *scheduled* fixture (draft/scheduled with a
 *      valid future scheduledAt). Unscheduled drafts are NOT upcoming.
 *   3. COMPLETED — most recently completed match.
 *   4. EMPTY — no eligible matches.
 *
 * This function is pure and does not mutate matches or statuses. It only
 * inspects fields and picks one.
 */
export function selectFeaturedMatch<T extends FeaturedMatchInput>(
  matches: T[],
  now: Date = new Date(),
): FeaturedMatch<T> | null {
  const normalized = matches.map((m) => ({
    match: m,
    status: normalizeStatus(m.status) as MatchStatus,
  }));

  // Priority 1: LIVE (genuine live; abandoned-with-activity is NOT live here)
  const live = normalized
    .filter((n) => n.status === "live")
    .sort((a, b) =>
      new Date(b.match.updatedAt ?? 0).getTime() -
      new Date(a.match.updatedAt ?? 0).getTime(),
    );
  if (live.length > 0) {
    return { match: live[0].match, category: "live" };
  }

  // Priority 2: UPCOMING — only drafts with a valid future scheduledAt
  const nowMs = now.getTime();
  const upcoming = normalized
    .filter((n) => {
      if (n.status !== "draft") return false;
      if (hasScoringActivity(n.match)) return false; // scoring drafts are live-ish, not upcoming
      const s = n.match.scheduledAt;
      if (!s) return false;
      const d = new Date(s);
      if (isNaN(d.getTime())) return false;
      return d.getTime() >= nowMs;
    })
    .sort((a, b) => {
      const aT = new Date(a.match.scheduledAt!).getTime();
      const bT = new Date(b.match.scheduledAt!).getTime();
      return aT - bT;
    });
  if (upcoming.length > 0) {
    return { match: upcoming[0].match, category: "upcoming" };
  }

  // Priority 3: COMPLETED — most recent by completedAt (fallback updatedAt)
  const completed = normalized
    .filter((n) => n.status === "completed")
    .sort((a, b) => {
      const aT = a.match.completedAt
        ? new Date(a.match.completedAt).getTime()
        : new Date(a.match.updatedAt ?? 0).getTime();
      const bT = b.match.completedAt
        ? new Date(b.match.completedAt).getTime()
        : new Date(b.match.updatedAt ?? 0).getTime();
      return bT - aT;
    });
  if (completed.length > 0) {
    return { match: completed[0].match, category: "completed" };
  }

  // Priority 4: EMPTY
  return null;
}

/**
 * Map a featured category to the My Matches tab it should open.
 */
export function categoryToMyMatchesTab(
  category: FeaturedCategory,
): "ongoing" | "upcoming" | "completed" | null {
  switch (category) {
    case "live":
      return "ongoing";
    case "upcoming":
      return "upcoming";
    case "completed":
      return "completed";
    case "empty":
      return null;
  }
}
